<template>
  <div>
    <v-toolbar color="page-toolbar">
      <v-toolbar-title class="text-body-large">Info débit — Rivières du Québec</v-toolbar-title>
    </v-toolbar>
    <v-container class="py-6 info-debit-content">
      <!-- Brand header (docs/BRAND.md) -->
      <header class="page-header mb-6">
        <img src="@/assets/brand/logo.svg" alt="" width="72" height="72" class="page-header__logo" />
        <div>
          <h1 class="page-header__title">
            Info débit — <span class="page-header__accent">La référence des rivières sportives du Québec</span>
          </h1>
          <p class="page-header__lead">
            Québec Eau Vive, anciennement Quebec Whitewater, est <strong>la référence en info-débit</strong> pour les
            pagayeurs du Québec. Consultez en temps réel les débits, tendances et recommandations de niveaux pour toutes
            les rivières sportives de la province.
          </p>
        </div>
      </header>

      <v-row dense class="mb-4">
        <v-col v-for="feature in features" :key="feature.title" cols="12" md="4">
          <v-card variant="outlined" class="feature-card h-100">
            <div class="feature-card__icon">
              <v-icon color="primary">{{ feature.icon }}</v-icon>
            </div>
            <div class="font-weight-bold mb-1">{{ feature.title }}</div>
            <div class="text-body-medium text-medium-emphasis">{{ feature.text }}</div>
          </v-card>
        </v-col>
      </v-row>

      <v-card variant="outlined" class="page-section">
        <h2><v-icon color="primary" class="mr-2">mdi-water-outline</v-icon>Qu'est-ce que l'info-débit ?</h2>
        <p>
          L'<strong>info-débit</strong> (ou <em>info debit</em>) désigne l'ensemble des informations relatives au débit
          d'une rivière, exprimé en mètres cubes par seconde (m³/s). Pour les pagayeurs pratiquant le kayak, le canot ou
          le raft, l'info-débit est indispensable : elle permet de savoir si une section de rivière est navigable, à
          quel niveau de difficulté, et si les conditions sont sécuritaires.
        </p>
        <p>
          Québec Eau Vive centralise l'info-débit de centaines de rivières sportives du Québec, en intégrant les données
          brutes des stations hydrométriques et en les enrichissant avec des recommandations issues de la communauté.
        </p>
      </v-card>

      <v-card variant="outlined" class="page-section">
        <h2><v-icon color="primary" class="mr-2">mdi-cogs</v-icon>Comment fonctionne notre info débit ?</h2>
        <p>
          Les données brutes proviennent principalement du Centre d'Expertise Hydrique du Québec (CEHQ) et
          d'Hydro-Québec. Notre modèle va cependant plus loin que la simple lecture d'une station :
        </p>
        <v-row dense class="mb-2">
          <v-col v-for="method in methods" :key="method.title" cols="12" sm="6">
            <div class="method-item">
              <v-icon color="primary" size="small" class="method-item__icon">{{ method.icon }}</v-icon>
              <div>
                <strong>{{ method.title }}</strong>
                <div class="text-body-medium">{{ method.text }}</div>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-card>

      <v-card variant="outlined" class="page-section">
        <h2>
          <v-icon color="primary" class="mr-2">mdi-compare-horizontal</v-icon>Pourquoi notre info-débit peut différer
          d'autres sources
        </h2>
        <p>
          Il est normal que l'info-débit affiché sur Québec Eau Vive diffère parfois d'autres plateformes. Nous
          utilisons une méthode distincte de l'approche classique, qui intègre notamment la tendance actuelle et les
          données de plusieurs stations. Une courte période d'adaptation peut être nécessaire, en particulier pour les
          sections de type creek qui réagissent rapidement.
        </p>
      </v-card>

      <h2 class="faq-title">
        <v-icon color="primary" class="mr-2">mdi-help-circle-outline</v-icon>Questions fréquentes sur l'info débit
      </h2>
      <div class="faq-section">
        <v-card variant="outlined" class="faq-item">
          <h3>Où trouver l'info débit des rivières du Québec ?</h3>
          <p>
            Québec Eau Vive (anciennement Quebec Whitewater) est la plateforme de référence pour l'info-débit des
            rivières sportives du Québec. Consultez la
            <router-link :to="{ name: 'rivieres' }">liste des rivières</router-link>
            pour accéder aux niveaux actuels, aux recommandations et aux tendances de débit.
          </p>
        </v-card>

        <v-card variant="outlined" class="faq-item">
          <h3>À quelle fréquence l'info débit est-il mis à jour ?</h3>
          <p>
            L'info-débit est mis à jour plusieurs fois par jour à partir des sources officielles (CEHQ, Hydro-Québec).
            La fréquence de mise à jour dépend de la disponibilité des données de chaque station hydrométrique.
          </p>
        </v-card>

        <v-card variant="outlined" class="faq-item">
          <h3>Qu'est-ce qu'un débit minimum et maximum recommandé ?</h3>
          <p>
            Le débit minimum est le niveau en dessous duquel la section est trop basse pour être pratiquée en sécurité
            ou avec plaisir. Le débit maximum est le seuil au-dessus duquel la rivière devient dangereuse ou réservée
            aux pagayeurs experts. Ces recommandations sont établies par notre communauté de pagayeurs expérimentés.
          </p>
        </v-card>

        <v-card variant="outlined" class="faq-item">
          <h3>L'info débit est-il disponible pour toutes les rivières du Québec ?</h3>
          <p>
            Notre base de données couvre les principales rivières sportives du Québec. Pour les rivières sans station
            hydrométrique directe, nous appliquons des méthodes d'extrapolation. Nous ajoutons régulièrement de
            nouvelles sections grâce au soutien de la communauté.
          </p>
        </v-card>
      </div>

      <!-- The page's one gold call-to-action (docs/BRAND.md) -->
      <v-card variant="flat" class="page-cta mt-8">
        <p class="text-title-large font-weight-bold mb-4">Prêt à consulter l'info débit de votre rivière ?</p>
        <div class="page-cta__actions">
          <v-btn color="gold" variant="flat" size="large" :to="{ name: 'rivieres' }">Voir toutes les rivières</v-btn>
          <v-btn variant="outlined" color="primary" size="large" :to="{ name: 'a-propos' }">
            Notre approche de l'info-débit
          </v-btn>
        </div>
      </v-card>
    </v-container>
  </div>
</template>

<script>
  import { applySeoMeta, signalPrerenderReady } from "@/utils/seo";

  const PAGE_TITLE = "Info débit | Info-débit des rivières du Québec";
  const PAGE_DESCRIPTION =
    "Trouvez l'info débit en temps réel pour les rivières sportives du Québec. Info-débit, recommandations de niveaux et tendances pour planifier vos sorties en kayak et canot.";

  export default {
    name: "InfoDebit",

    data() {
      return {
        features: [
          {
            icon: "mdi-chart-line",
            title: "Info débit en temps réel",
            text: "Niveaux actualisés plusieurs fois par jour à partir des stations hydrométriques du CEHQ et d'Hydro-Québec.",
          },
          {
            icon: "mdi-thumb-up-outline",
            title: "Recommandations de débit",
            text: "Débits minimum et maximum recommandés par section, validés par la communauté de pagayeurs.",
          },
          {
            icon: "mdi-trending-up",
            title: "Tendances de débit",
            text: "Visualisez si le débit monte, descend ou est stable — une donnée essentielle pour la sécurité.",
          },
        ],
        methods: [
          {
            icon: "mdi-vector-polyline",
            title: "Extrapolation intelligente",
            text: "Pour les rivières sans station hydrométrique directe, notre algorithme extrapole le débit à partir de stations voisines du même bassin hydrographique.",
          },
          {
            icon: "mdi-trending-up",
            title: "Tendance de débit",
            text: "Le débit est-il en hausse, en baisse ou stable ? Cette info est affichée en temps réel sur chaque section.",
          },
          {
            icon: "mdi-history",
            title: "Historique hydrique",
            text: "L'analyse des données historiques permet de contextualiser le débit actuel par rapport aux moyennes saisonnières.",
          },
          {
            icon: "mdi-account-group-outline",
            title: "Validation communautaire",
            text: "Les pagayeurs contribuent à affiner les recommandations de débit après chaque sortie.",
          },
        ],
      };
    },

    mounted() {
      this.$nextTick(() => {
        applySeoMeta({
          title: PAGE_TITLE,
          description: PAGE_DESCRIPTION,
          canonicalPath: "/info-debit",
          structuredData: this.buildStructuredData(),
        });
        signalPrerenderReady();
      });
    },

    methods: {
      buildStructuredData() {
        return [
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
              {
                "@type": "Question",
                name: "Où trouver l'info débit des rivières du Québec ?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Québec Eau Vive (anciennement Quebec Whitewater) est la plateforme de référence pour l'info-débit des rivières sportives du Québec. Vous y trouverez les niveaux actuels, les recommandations de débit minimum et maximum, ainsi que les tendances de débit pour planifier vos sorties en kayak.",
                },
              },
              {
                "@type": "Question",
                name: "À quelle fréquence l'info débit est-il mis à jour ?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "L'info-débit est mis à jour plusieurs fois par jour à partir des sources officielles du Centre d'Expertise Hydrique du Québec (CEHQ) et d'Hydro-Québec.",
                },
              },
              {
                "@type": "Question",
                name: "Qu'est-ce qu'un débit minimum et maximum recommandé ?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Le débit minimum est le niveau en dessous duquel la section est trop basse pour être pratiquée en sécurité. Le débit maximum est le seuil au-dessus duquel la rivière devient dangereuse. Ces recommandations sont établies par la communauté de pagayeurs de Québec Eau Vive.",
                },
              },
              {
                "@type": "Question",
                name: "L'info débit est-il disponible pour toutes les rivières du Québec ?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Québec Eau Vive couvre les principales rivières sportives du Québec. Pour les rivières sans station hydrométrique directe, des méthodes d'extrapolation sont appliquées pour fournir une info-débit fiable.",
                },
              },
            ],
          },
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: PAGE_TITLE,
            description: PAGE_DESCRIPTION,
            url: "https://quebecwhitewater.com/info-debit",
            inLanguage: "fr-CA",
            breadcrumb: {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Accueil", item: "https://quebecwhitewater.com/" },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: "Info débit",
                  item: "https://quebecwhitewater.com/info-debit",
                },
              ],
            },
          },
        ];
      },
    },
  };
</script>

<style lang="scss" scoped>
  .info-debit-content {
    max-width: 980px;
    line-height: 1.6;
  }

  .page-header {
    display: flex;
    align-items: center;
    gap: 20px;
  }

  .page-header__logo {
    flex: none;
  }

  .page-header__title {
    margin: 0 0 6px;
    font-size: 1.9rem;
    font-weight: 800;
    letter-spacing: -0.4px;
    line-height: 1.2;
    color: var(--qww-hero-wordmark);
  }

  .page-header__accent {
    font-weight: 400;
    color: var(--qww-hero-kicker);
  }

  .page-header__lead {
    margin: 0;
    font-size: 1.05rem;
    color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  }

  .feature-card {
    padding: 18px 20px;
  }

  /* Icon on the pale drop colour */
  .feature-card__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    margin-bottom: 10px;
    border-radius: 50%;
    background: rgb(var(--v-theme-primary-tint));
  }

  .page-section {
    padding: 20px 24px 12px;
    margin-bottom: 16px;
  }

  h2 {
    display: flex;
    align-items: center;
    margin: 0 0 0.75rem;
    font-size: 1.25rem;
    line-height: 1.3;
  }

  h3 {
    font-size: 1.05rem;
    font-weight: 600;
    margin-bottom: 0.4rem;
  }

  p {
    margin-bottom: 0.8rem;
  }

  .method-item {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 6px 0;
  }

  .method-item__icon {
    margin-top: 3px;
  }

  .faq-title {
    margin-top: 1.75rem;
  }

  .faq-item {
    padding: 14px 20px 6px;
    margin-bottom: 12px;
    border-left: 4px solid rgb(var(--v-theme-primary));
  }

  /* Pale gold card with the page's gold call-to-action */
  .page-cta {
    padding: 24px;
    text-align: center;
    background: rgb(var(--v-theme-gold-tint));
    border: 1px solid rgba(var(--v-theme-gold), 0.5);
  }

  .page-cta__actions {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 12px;
  }

  @media (max-width: 600px) {
    .page-header {
      align-items: flex-start;
      gap: 14px;
    }

    .page-header__logo {
      width: 52px;
      height: 52px;
    }

    .page-header__title {
      font-size: 1.4rem;
    }

    .page-section {
      padding: 16px 16px 8px;
    }
  }
</style>
